import { first } from '@quromedical/utils';
import { z } from 'zod';
import { type SupportedResourceType, supportedResourceSet } from '../resources';

import type {
  ValueAttachment,
  ValueCodeableConcept,
  ValueDateTime,
  ValuePeriod,
  ValueQuantity,
  ValueRange,
  ValueRatio,
  ValueSampledData,
  ValueString,
  ValueTime,
  ValueType,
  ValueTypeModel,
  ValueTypeNames,
  ValueTypeRecord,
} from './schema';

export const UUIDSchema = z.string().uuid();

export const BaseUUIDSearchSchema = z.string().superRefine((val, ctx) => {
  const ids = val.split(',');
  const parsed = UUIDSchema.array().safeParse(ids);

  if (parsed.success) {
    return val;
  }

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message:
      'Invalid UUID detected, must be a string of valid UUIDs comma separated',
    path: [],
  });
});

export const UUIDSearchSchema = z.object({
  ids: BaseUUIDSearchSchema,
});

// TODO: this should be moved to domain once a structure is agreed upon

export const ReferenceViewSchema = z.object({
  id: UUIDSchema,
  // TODO: this should be a fhir resource type
  type: z.string(),
  name: z.string(),
});

export const UUIDInputSchema = z.object({
  id: UUIDSchema,
});

export const ReferenceSchema = z.string().refine((value) => {
  const parts = value.split('/');
  // TODO: validate resourceType
  if (parts.length !== 2) {
    return false;
  }

  const [resourceType, id] = parts;

  if (!supportedResourceSet.has(resourceType as SupportedResourceType)) {
    return false;
  }

  const parsedId = UUIDSchema.safeParse(id);

  return parsedId.success;
});

export const VersionedReferenceSchema = z.string().refine((value) => {
  const parts = value.split('/');
  // TODO: validate resourceType
  if (parts.length !== 4) {
    return false;
  }

  const [resourceType, id, history, versionId] = parts;

  if (!supportedResourceSet.has(resourceType as SupportedResourceType)) {
    return false;
  }

  if (history !== '_history') {
    return false;
  }

  if (!versionId) {
    return false;
  }

  const parsedId = UUIDSchema.safeParse(id);

  return parsedId.success;
});

export const DisplayReferenceSchema = z.object({
  id: UUIDSchema.optional(),
  reference: ReferenceSchema,
  display: z.string().optional(),
});

export type ReferenceView = z.infer<typeof ReferenceViewSchema>;
export type DisplayReference = z.infer<typeof DisplayReferenceSchema>;

const valueQuantityModel: ValueTypeModel<ValueQuantity> = (
  input: ValueQuantity,
) => {
  return {
    toString: () => `${input.valueQuantity.value} ${input.valueQuantity.unit}`,
    toValue: () => input,
  };
};

const valueCodeableConceptModel: ValueTypeModel<ValueCodeableConcept> = (
  input: ValueCodeableConcept,
) => {
  return {
    toString: () =>
      input.valueCodeableConcept.text ||
      first(input.valueCodeableConcept.coding)?.display ||
      '',
    toValue: () => input,
  };
};

const valueStringModel: ValueTypeModel<ValueString> = (input: ValueString) => {
  return {
    toString: () => input.valueString,
    toValue: () => input,
  };
};

const valueRangeModel: ValueTypeModel<ValueRange> = (input: ValueRange) => {
  return {
    toString: () =>
      `${input.valueRange.low?.value || ''} - ${input.valueRange.high?.value || ''} ${
        input.valueRange.low?.unit || ''
      }`,
    toValue: () => input,
  };
};

const valueRatioModel: ValueTypeModel<ValueRatio> = (input: ValueRatio) => {
  return {
    toString: () =>
      `${input.valueRatio.numerator?.value || ''} / ${input.valueRatio.denominator?.value || ''}`,
    toValue: () => input,
  };
};

const valueSampledDataModel: ValueTypeModel<ValueSampledData> = (
  input: ValueSampledData,
) => {
  return {
    toString: () => input.valueSampledData.data || '',
    toValue: () => input,
  };
};

const valueAttachmentModel: ValueTypeModel<ValueAttachment> = (
  input: ValueAttachment,
) => {
  return {
    toString: () => input.valueAttachment.title || '',
    toValue: () => input,
  };
};

const valueTimeModel: ValueTypeModel<ValueTime> = (input: ValueTime) => {
  return {
    toString: () => input.valueTime,
    toValue: () => input,
  };
};

const valueDateTimeModel: ValueTypeModel<ValueDateTime> = (
  input: ValueDateTime,
) => {
  return {
    toString: () => input.valueDateTime,
    toValue: () => input,
  };
};

const valuePeriodModel: ValueTypeModel<ValuePeriod> = (input: ValuePeriod) => {
  return {
    toString: () => `${input.valuePeriod.start} - ${input.valuePeriod.end}`,
    toValue: () => input,
  };
};

const valueTypeMapping: Record<
  keyof ValueTypeNames,
  ValueTypeModel<ValueType>
> = {
  valueQuantity: valueQuantityModel as ValueTypeModel<ValueType>,
  valueCodeableConcept: valueCodeableConceptModel as ValueTypeModel<ValueType>,
  valueString: valueStringModel as ValueTypeModel<ValueType>,
  valueRange: valueRangeModel as ValueTypeModel<ValueType>,
  valueRatio: valueRatioModel as ValueTypeModel<ValueType>,
  valueSampledData: valueSampledDataModel as ValueTypeModel<ValueType>,
  valueAttachment: valueAttachmentModel as ValueTypeModel<ValueType>,
  valueTime: valueTimeModel as ValueTypeModel<ValueType>,
  valueDateTime: valueDateTimeModel as ValueTypeModel<ValueType>,
  valuePeriod: valuePeriodModel as ValueTypeModel<ValueType>,
};

export const getValueTypeModel = (
  input: ValueType,
): ValueTypeRecord<ValueType> => {
  const keys = Object.keys(input) as (keyof ValueType)[];
  const key = keys[0];
  const handler = valueTypeMapping[key as keyof ValueTypeNames];

  return handler(input);
};
